//#region Imports
import { createSingleEventEmitter, areObjectsShallowEqual } from "@anydo/core";
import type {
  DeepReadonlyT,
  AuthStatusCode,
  PromiseStatus,
  IReadonlyUserDetailsRecord,
  IReadonlyUserSubscriptionStatusRecord,
} from "@anydo/interfaces";
//#endregion

export type AuthReactAdapterStateStateT = {
  isLoggedIn: boolean;
  isNewUser: boolean;
  isPremiumUser: boolean;
  userDetails: IReadonlyUserDetailsRecord | null;
  userSubscription: IReadonlyUserSubscriptionStatusRecord | null;
  //
  status: AuthStatusCode;
  /** Is updating is locked by block action? */
  isPending: boolean;
  isStatusError: boolean;
  // Post
  isPostPremiumStripeInvoicesVisible: boolean;
  postPremiumStripeInvoicesStatus: PromiseStatus;
};

export type ReadonlyAuthReactAdapterStateStateT = DeepReadonlyT<AuthReactAdapterStateStateT>;

type UpdateT =
  | ((
      state: ReadonlyAuthReactAdapterStateStateT,
    ) => Partial<ReadonlyAuthReactAdapterStateStateT> | ReadonlyAuthReactAdapterStateStateT)
  | Partial<ReadonlyAuthReactAdapterStateStateT>;

export class AuthReactAdapterState {
  private _updates: Array<UpdateT> = [];
  private _stateEmitter = createSingleEventEmitter();
  private _state: ReadonlyAuthReactAdapterStateStateT;

  constructor(state: ReadonlyAuthReactAdapterStateStateT) {
    this._state = state;
  }

  destroy() {
    this._stateEmitter.off();
    this._updates = undefined!;
    this._state = undefined!;
    this._stateEmitter = undefined!;
  }

  //#region Private
  private _applyNewState(state: ReadonlyAuthReactAdapterStateStateT) {
    if (!areObjectsShallowEqual(this._state, state)) {
      this._state = state;
      this._stateEmitter.emit();
    }
  }
  //#endregion

  //#region Public
  onStateChange = this._stateEmitter.on;
  onceStateChange = this._stateEmitter.once;

  getState(): ReadonlyAuthReactAdapterStateStateT {
    return this._state;
  }

  pushUpdate(update: UpdateT) {
    this._updates.push(update);
  }

  isEmpty() {
    return this._updates.length === 0;
  }

  updateImmediate(update: UpdateT) {
    const partialState = typeof update === "function" ? update(this._state) : update;

    this._applyNewState({
      ...this._state,
      ...partialState,
    });
  }

  flushUpdates() {
    if (this._updates.length === 0) return;

    let newState = { ...this._state };

    let update: UpdateT | undefined;
    while ((update = this._updates.shift())) {
      // Assignment is only for type checking
      newState = Object.assign(newState, typeof update === "function" ? update(newState) : update);
    }

    this._applyNewState(newState);
  }
  //#endregion
}
